import { LEVEL, LEVEL_PROPERTIES } from '@/interfaces/level.interface'
import { orderOnLevel } from '../utils/sort'
import { getRequestGateway } from '@/services/api.service'

export async function getFloorPlansForLevelV1(id: string): Promise<any> {
  return getRequestGateway('core/v1/levels/' + id + '/floorplans')
}

export async function getLevelv1(id: string): Promise<any> {
  return getRequestGateway('core/v1/levels/' + id)
}

export async function getLevelsv1(id: string): Promise<LEVEL_PROPERTIES[]> {
  return (await getRequestGateway('core/v1/levels?constructionId=' + id)).data as LEVEL_PROPERTIES[]
}

async function getLevelsGeoV1(id: string): Promise<LEVEL[]> {
  return (await getRequestGateway('core/v1/levels/geojson?constructionId=' + id)).data as LEVEL[]
}

// `geojson` only contains a feature for a level that actually has geometry saved
// (core/v1/levels/geojson only returns documents that exist in the geometry collection -- a
// level with none simply has no entry there, it's not returned with a null geometry). Iterating
// over `geojson` as the base therefore silently dropped every geometry-less level from the
// result -- invisible in the floor picker, the map, asset dashboards, search, everywhere this
// list feeds. Iterating over `levelProperties` (every level for this construction) instead, and
// falling back to the building's own footprint per-level, keeps every level present.
function joinLevelData(
  geojson: LEVEL[],
  levelProperties: LEVEL_PROPERTIES[],
  buildingGeometry: GeoJSON.Polygon | GeoJSON.MultiPolygon
): LEVEL[] {
  const geoByLevelId = new Map(geojson.map((feature) => [feature.properties.belongsToId, feature]))
  return levelProperties.map((level) => {
    const feature = geoByLevelId.get(level.id)
    if (feature) {
      feature.properties = level
      feature.id = level.id
      feature.properties.belongsToId = level.id
      return feature
    }
    return {
      id: level.id,
      geometry: buildingGeometry,
      properties: { ...level, belongsToId: level.id, geometryFromBuilding: true },
      type: 'Feature' as const
    }
  })
}

function updateHeightAndElevation(levels: LEVEL[]): LEVEL[] {
  let additionalHeight = 0
  const gapHeight = 0.5 // Height of the gap between levels
  let currentElevation = 0 // Tracks the elevation for when the elevation property is missing for a level
  let minElevation: number

  return levels.map((level, counter) => {
    if (counter === 0) {
      minElevation = level.properties.elevation ?? 0
      level.properties.elevation -= minElevation
    } else if (!level.properties.elevation) {
      level.properties.elevation = currentElevation
    } else {
      level.properties.elevation -= minElevation!
      level.properties.elevation += gapHeight + additionalHeight
    }
    if (!level.properties.height) {
      level.properties.height = 3
    }
    if (level.properties.height < 3) {
      const newHeight = level.properties.elevation + 3
      additionalHeight += newHeight - level.properties.height
      level.properties.height = newHeight
    } else {
      level.properties.height = level.properties.elevation - gapHeight + level.properties.height
    }
    currentElevation = level.properties.height + gapHeight
    return level
  })
}

export async function getLevelsForBuilding(
  buildingId: string,
  buildingGeometry: GeoJSON.Polygon | GeoJSON.MultiPolygon,
  getFloorPlans: boolean = true,
  bypassGeometry: boolean = false
): Promise<any> {
  try {
    const geojson = await getLevelsGeoV1(buildingId)
    if (!geojson && !bypassGeometry) return null
    const levelProperties = await getLevelsv1(buildingId)
    let levels = joinLevelData(geojson, levelProperties, buildingGeometry)
    levels.sort(orderOnLevel)

    if (getFloorPlans) {
      levels = await Promise.all(
        levels.map((level) =>
          getFloorPlansForLevelV1(level.id).then(
            (floorplans: any) =>
              (level.properties.hasFloorPlans = floorplans ? floorplans.data : [])
          )
        )
      )
    }

    levels = updateHeightAndElevation(levels)

    return {
      type: 'geojson',
      data: {
        id: 'sourceLevel',
        type: 'FeatureCollection',
        features: levels
      }
    }
  } catch (e: any) {
    console.error(e)
    throw new Error('Could not fetch levels', { cause: e })
  }
}
